"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AccessibilityControls from "@/components/accessibility-controls/accessibility-controls";
import type { AccessibilityControlsLabels } from "@/components/accessibility-controls/accessibility-controls";
import { useTranslations } from "next-intl";
import type { CameraAnimatorRef } from "./camera/camera-animator";
import { INITIAL_CAMERA_POSITION } from "../constants";
import type { ReactNode } from "react";

// Delta de rotación en radianes por paso (vista general)
const ROTATION_STEP = 0.75;
// Delta de zoom (unidades de distancia de cámara) por paso (vista general)
const ZOOM_STEP = 6;
// Delta de rotación en radianes por paso (modo seguimiento de satélite)
const FOLLOW_ROTATION_STEP = 0.08;
// Delta de zoom por paso (modo seguimiento de satélite)
const FOLLOW_ZOOM_STEP = 1.5;
// Distancia mínima y máxima de la cámara (coinciden con CameraControls)
const MIN_DISTANCE = 15;
const MAX_DISTANCE = 55;
// Distancia mínima y máxima en modo seguimiento (coinciden con camera-animator)
const FOLLOW_MIN_DISTANCE = 2;
const FOLLOW_MAX_DISTANCE = 50;
// Factor de interpolación por frame para animación suave (ease-out)
const LERP_FACTOR = 0.15;
// Umbral mínimo para considerar una animación completada
const ANIM_THRESHOLD = 0.0005;
interface GlobeAccessibilityControlsProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  controlsRef: React.RefObject<any>;
  cameraAnimatorRef: React.RefObject<CameraAnimatorRef | null>;
  selectedSatelliteIndex: number | null;
  onTextualAlternative?: () => void;
  legendContent?: ReactNode;
}

/**
 * Wrapper que conecta los controles de accesibilidad genéricos
 * con la API de Three.js OrbitControls y CameraAnimator.
 */
export default function GlobeAccessibilityControls({
  controlsRef,
  cameraAnimatorRef,
  selectedSatelliteIndex,
  onTextualAlternative,
  legendContent
}: GlobeAccessibilityControlsProps) {
  const t = useTranslations("AccessibilityControls");
  const [zoomPercentage, setZoomPercentage] = useState(0);
  const isFollowing = selectedSatelliteIndex !== null;

  // Ref para leer isFollowing dentro del bucle rAF sin recrear callbacks
  const isFollowingRef = useRef(isFollowing);
  isFollowingRef.current = isFollowing;

  // Estado de animación suave — acumula deltas pendientes de aplicar
  const remainingRef = useRef({
    azimuth: 0,
    polar: 0,
    distance: 0
  });
  const rafRef = useRef<number | null>(null);

  // Calcular porcentaje de zoom según el modo activo
  const updateZoom = useCallback(() => {
    if (isFollowingRef.current) {
      const state = cameraAnimatorRef.current?.getFollowCameraState();
      if (!state) return;
      const pct = (FOLLOW_MAX_DISTANCE - state.distance) / (FOLLOW_MAX_DISTANCE - FOLLOW_MIN_DISTANCE) * 100;
      setZoomPercentage(Math.round(Math.max(0, Math.min(100, pct))));
    } else {
      const controls = controlsRef.current;
      if (!controls) return;
      const distance = controls.getDistance();
      const pct_0 = (MAX_DISTANCE - distance) / (MAX_DISTANCE - MIN_DISTANCE) * 100;
      setZoomPercentage(Math.round(Math.max(0, Math.min(100, pct_0))));
    }
  }, [controlsRef, cameraAnimatorRef]);

  // Actualizar zoom inicial y escuchar cambios de OrbitControls (rueda del ratón, drag, etc.)
  useEffect(() => {
    const id = requestAnimationFrame(updateZoom);
    const controls_0 = controlsRef.current;
    if (controls_0) {
      controls_0.addEventListener("change", updateZoom);
    }
    return () => {
      cancelAnimationFrame(id);
      if (controls_0) {
        controls_0.removeEventListener("change", updateZoom);
      }
    };
  }, [updateZoom, controlsRef]);

  // En modo seguimiento, polling para actualizar el porcentaje de zoom
  // ya que OrbitControls está deshabilitado y no emite eventos "change"
  useEffect(() => {
    if (!isFollowing) return;
    const id_0 = setInterval(updateZoom, 100);
    return () => clearInterval(id_0);
  }, [isFollowing, updateZoom]);

  // Bucle de animación suave — aplica una fracción del delta pendiente cada frame
  const tick = useCallback(() => {
    const rem = remainingRef.current;
    if (isFollowingRef.current) {
      const state_0 = cameraAnimatorRef.current?.getFollowCameraState();
      if (state_0) {
        const dT = rem.azimuth * LERP_FACTOR;
        const dP = rem.polar * LERP_FACTOR;
        const dD = rem.distance * LERP_FACTOR;
        state_0.theta += dT;
        state_0.phi = Math.max(0.05, Math.min(Math.PI / 2 - 0.05, state_0.phi + dP));
        state_0.distance = Math.max(FOLLOW_MIN_DISTANCE, Math.min(FOLLOW_MAX_DISTANCE, state_0.distance + dD));
        rem.azimuth -= dT;
        rem.polar -= dP;
        rem.distance -= dD;
      }
    } else {
      const controls_1 = controlsRef.current;
      if (controls_1) {
        const dA = rem.azimuth * LERP_FACTOR;
        const dP_0 = rem.polar * LERP_FACTOR;
        const dD_0 = rem.distance * LERP_FACTOR;
        if (Math.abs(dA) > ANIM_THRESHOLD) controls_1.setAzimuthalAngle(controls_1.getAzimuthalAngle() + dA);
        if (Math.abs(dP_0) > ANIM_THRESHOLD) controls_1.setPolarAngle(controls_1.getPolarAngle() + dP_0);
        if (Math.abs(dD_0) > ANIM_THRESHOLD) {
          const curDist = controls_1.getDistance();
          const newDist = Math.max(MIN_DISTANCE, Math.min(MAX_DISTANCE, curDist + dD_0));
          const camera = controls_1.object;
          const target = controls_1.target;
          const dir = camera.position.clone().sub(target).normalize();
          camera.position.copy(target).addScaledVector(dir, newDist);
        }
        controls_1.update();
        rem.azimuth -= dA;
        rem.polar -= dP_0;
        rem.distance -= dD_0;
      }
    }
    updateZoom();
    const hasRemaining = Math.abs(rem.azimuth) > ANIM_THRESHOLD || Math.abs(rem.polar) > ANIM_THRESHOLD || Math.abs(rem.distance) > ANIM_THRESHOLD;
    if (hasRemaining) {
      rafRef.current = requestAnimationFrame(tick);
    } else {
      rem.azimuth = 0;
      rem.polar = 0;
      rem.distance = 0;
      rafRef.current = null;
    }
  }, [controlsRef, cameraAnimatorRef, updateZoom]);
  const startAnimation = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(tick);
  }, [tick]);

  // Limpiar animación al cambiar de modo o desmontar
  useEffect(() => {
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      remainingRef.current = {
        azimuth: 0,
        polar: 0,
        distance: 0
      };
    };
  }, [isFollowing]);
  const labels = useMemo<AccessibilityControlsLabels>(() => ({
    panUp: t("panUp"),
    panDown: t("panDown"),
    panLeft: t("panLeft"),
    panRight: t("panRight"),
    zoomIn: t("zoomIn"),
    zoomOut: t("zoomOut"),
    resetView: t("resetView"),
    textualAlternative: t("textualAlternative"),
    info: t("info"),
    actionsTitle: t("actionsTitle"),
    legendTitle: t("legendTitle")
  }), [t]);
  // Acumular delta de rotación horizontal y animar suavemente
  const rotateAzimuth = useCallback((delta: number) => {
    remainingRef.current.azimuth += delta;
    startAnimation();
  }, [startAnimation]);

  // Acumular delta de rotación vertical y animar suavemente
  const rotatePolar = useCallback((delta_0: number) => {
    remainingRef.current.polar += delta_0;
    startAnimation();
  }, [startAnimation]);

  // Acumular delta de zoom y animar suavemente
  const dolly = useCallback((delta_1: number) => {
    remainingRef.current.distance += delta_1;
    startAnimation();
  }, [startAnimation]);
  const rotStep = isFollowing ? FOLLOW_ROTATION_STEP : ROTATION_STEP;
  const zoomStep = isFollowing ? FOLLOW_ZOOM_STEP : ZOOM_STEP;
  const onPanUp = useCallback(() => rotatePolar(-rotStep), [rotatePolar, rotStep]);
  const onPanDown = useCallback(() => rotatePolar(rotStep), [rotatePolar, rotStep]);
  const onPanLeft = useCallback(() => rotateAzimuth(-rotStep), [rotateAzimuth, rotStep]);
  const onPanRight = useCallback(() => rotateAzimuth(rotStep), [rotateAzimuth, rotStep]);
  const onZoomIn = useCallback(() => dolly(-zoomStep), [dolly, zoomStep]);
  const onZoomOut = useCallback(() => dolly(zoomStep), [dolly, zoomStep]);
  const onReset = useCallback(() => {
    // Cancelar animación en curso
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    remainingRef.current = {
      azimuth: 0,
      polar: 0,
      distance: 0
    };

    // En modo seguimiento, animar suavemente la órbita de vuelta a los valores por defecto
    if (isFollowingRef.current) {
      const state_1 = cameraAnimatorRef.current?.getFollowCameraState();
      if (state_1) {
        remainingRef.current.azimuth = -state_1.theta;
        remainingRef.current.polar = Math.PI / 6 - state_1.phi;
        remainingRef.current.distance = 8 - state_1.distance;
        startAnimation();
      }
      return;
    }
    const controls_2 = controlsRef.current;
    if (!controls_2) return;

    // Si hay un CameraAnimator disponible, usar su método resetCamera (ya tiene animación propia)
    if (cameraAnimatorRef.current) {
      cameraAnimatorRef.current.resetCamera();
      requestAnimationFrame(updateZoom);
      return;
    }

    // Fallback: restablecer cámara a posición inicial manualmente
    const camera_0 = controls_2.object;
    camera_0.position.set(...INITIAL_CAMERA_POSITION);
    controls_2.update();
    updateZoom();
  }, [controlsRef, cameraAnimatorRef, updateZoom, startAnimation]);
  return <AccessibilityControls onPanUp={onPanUp} onPanDown={onPanDown} onPanLeft={onPanLeft} onPanRight={onPanRight} onZoomIn={onZoomIn} onZoomOut={onZoomOut} onReset={onReset} onTextualAlternative={onTextualAlternative} zoomPercentage={zoomPercentage} labels={labels} legendContent={legendContent} />;
}